import { css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { IPhoneCall } from '../../../../extViewmodels';
import { copyToClipboard } from '../../../../models/UiUtils';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { Button } from '../../../../web/components-v2/Button';
import { THEME } from '../../../../web/components-v2/Theme/theme';
import { SimpleTooltip, Tooltip, TooltipContent, TooltipTrigger } from '../../../../web/components/Tooltip';
import { DuplicateIcon } from '../../../../web/components/svgs/icons/DuplicateIcon';
import { PlayIcon } from '../../../../web/components/svgs/icons/PlayIcon';
import { ChatUserIcon } from '../../../components/svgs/icons/ChatUserIcon';
import { ChatUsersIcon } from '../../../components/svgs/icons/ChatUsersIcon';
import { TranscriptionIcon } from '../../../components/svgs/icons/TranscriptionIcon';
import { styleSheet } from './styles';

// Radix closes a tooltip when its trigger is pressed; these are labels, so pressing one should leave it showing.
const RecordingIndicator: React.FC<React.PropsWithChildren<{ label: string }>> = ({ label, children }) => {
	const iconRef = React.useRef<HTMLSpanElement>(null);
	const keepOpen = (event: { preventDefault: () => void }) => event.preventDefault();
	return (
		<Tooltip>
			<TooltipTrigger asChild onClick={keepOpen} onPointerDown={keepOpen}>
				<span ref={iconRef} className={css(styleSheet.recordingIcon)} aria-label={label} role='img'>
					{children}
				</span>
			</TooltipTrigger>
			<TooltipContent
				onPointerDownOutside={event => {
					if (iconRef.current?.contains(event.target as Node)) {
						event.preventDefault();
					}
				}}
			>
				{label}
			</TooltipContent>
		</Tooltip>
	);
};

interface IProps {
	phoneCall?: IPhoneCall;
	onPlay: () => void;
	showTranscription?: boolean;
}

export const RecordingCell: React.FC<IProps> = ({ phoneCall, onPlay, showTranscription = true }) => {
	const userSession = useUserSession();
	if (!phoneCall?.recording?.publicUrl) {
		return <span className={css(styleSheet.muted)}>None</span>;
	}

	const canCopyLink = !!userSession.account?.features?.aida?.allowAnonymousAccessToCallRecording;
	const onCopyLink = () => {
		const url = `${userSession.webServiceHelper?.baseUrl}/PhoneCall/${phoneCall.id}/Download`;
		if (copyToClipboard(url)) {
			toast.success('Link copied to clipboard');
		} else {
			toast.error('Unable to copy link');
		}
	};
	const bothSidesRecorded = (phoneCall.recordingChannels ?? 0) > 1;

	return (
		<div className={css(styleSheet.recording, styleSheet.aboveRow)}>
			<Button type='button' variant='primary' size='sm' onClick={onPlay}>
				<PlayIcon width={16} height={16} />
				Play
			</Button>
			{canCopyLink ? (
				<SimpleTooltip title='Copy recording link'>
					<Button type='button' variant='ghost' size='icon' aria-label='Copy recording link' onClick={onCopyLink}>
						<DuplicateIcon fillColor='currentColor' />
					</Button>
				</SimpleTooltip>
			) : null}
			{showTranscription && phoneCall.transcriptionId ? (
				<>
					<RecordingIndicator label='Transcribed'>
						<TranscriptionIcon className={css(styleSheet.recordingIconSvg)} fillColor={THEME.colors.icon.primary} />
					</RecordingIndicator>
					<RecordingIndicator label={bothSidesRecorded ? 'All parties recorded' : 'Rep recorded'}>
						{bothSidesRecorded ? (
							<ChatUsersIcon className={css(styleSheet.recordingIconSvg)} fillColor={THEME.colors.icon.secondary} />
						) : (
							<ChatUserIcon className={css(styleSheet.recordingIconSvg)} fillColor={THEME.colors.icon.primary} />
						)}
					</RecordingIndicator>
				</>
			) : null}
		</div>
	);
};
